// Shared fail-closed security helpers for browser and server adapters.
// They keep hard-failure behavior consistent across guard consumers.

// Exposes the shared security error used by guard-backed adapters.
export class LuisSecurityError extends Error {
  readonly code = 'security-check-failed'

  constructor(message: string) {
    super(message)
    this.name = 'LuisSecurityError'
  }
}

// Exposes one dependency assertion helper for fail-closed security checks.
export function assertSecurityDependency(condition: boolean, dependencyName: string): void {
  // Stop the current security flow when one required dependency is unavailable.
  if (!condition) {
    throw new LuisSecurityError(`[LentyStyle][Security] Required dependency is unavailable: ${dependencyName}.`)
  } else {
    // Continue when the required dependency is available.
  }
}

// Exposes one rethrow helper that bubbles shared security failures only.
export function rethrowSecurityError(error: unknown): void {
  // Re-throw the current error only when it is one shared security failure.
  if (error instanceof LuisSecurityError) {
    throw error
  } else {
    // Ignore non-security errors in fallback branches.
  }
}

// Exposes one fail-closed wrapper for unexpected security-check exceptions.
export function failClosedSecurityCheck(checkName: string, error: unknown): never {
  const message = error instanceof Error ? error.message : String(error)
  // Throw one normalized shared security error for the failed check.
  throw new LuisSecurityError(`[LentyStyle][Security] ${checkName} failed: ${message}`)
}
